import type { PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import type { TextStyleValue } from '@elfsight/configurator-contracts';
import { CardBlock } from '@elfsight/widget-ui';
import { useTranslation } from 'react-i18next';

interface DescriptionProps extends PropsWithChildren {
  isShowTitle?: boolean;
  isClamp?: boolean;
  fontSize?: number;
  color?: string;
  linkColor?: string;
  headlineColor?: string;
  headlineStyle?: TextStyleValue;
}

export const Description = ({
  children,
  isShowTitle,
  isClamp = true,
  fontSize,
  color,
  linkColor,
  headlineColor,
  headlineStyle
}: DescriptionProps) => {
  const { t } = useTranslation();

  return (
    <Component>
      {isShowTitle && (
        <Title
          _fontSize={headlineStyle?.fontSize}
          _color={headlineColor}
          _fontWeight={headlineStyle?.fontWeight}
        >
          {t('Overview')}
        </Title>
      )}
      <Text
        _isClamp={isClamp}
        _fontSize={fontSize}
        _color={color}
        _linkColor={linkColor}
        dangerouslySetInnerHTML={{
          __html: children as string
        }}
      />
    </Component>
  );
};

const Component = styled(CardBlock)`
  flex-direction: column;
  align-items: flex-start;
  .ql-align-center {
    text-align: center;
  }
  .ql-align-justify {
    text-align: justify;
  }
  .ql-align-right {
    text-align: right;
  }
`;
const Text = styled.div<{
  _fontSize?: number;
  _isClamp?: boolean;
  _linkColor?: string;
  _color?: string;
}>`
  max-width: 100%;
  font-size: ${({ _fontSize }) => _fontSize}px;
  line-height: ${({ _fontSize }) => Number(_fontSize) * 1.28571}px;
  color: ${({ _color }) => _color};

  ${({ _isClamp, _fontSize }) =>
    _isClamp &&
    css`
      max-height: ${Number(_fontSize) * 2.5714}px;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      & p,
      ol,
      ul {
        margin: 0;
      }
    `}

  & a {
    color: ${({ _linkColor }) => _linkColor};
  }
`;
const Title = styled.div<{
  _fontSize?: number;
  _color?: string;
  _fontWeight?: TextStyleValue['fontWeight'];
}>`
  font-size: ${({ _fontSize }) => _fontSize}px;
  font-weight: ${({ _fontWeight }) => _fontWeight};
  line-height: ${({ _fontSize }) => Number(_fontSize) * 1.5}px;
  color: ${({ _color }) => _color};
  margin-bottom: ${({ _fontSize }) => Number(_fontSize) * 0.5}px;
`;
